// Growem — count-up rest timer overlay. A 3-zone range meter (recuperando · en rango · de sobra)
// with mín/ideal/máx markers; the athlete stops it with "Listo", which records the actual rest.

import { useUI } from '../state/ui'
import { useStore } from '../state/store'
import { frac, idealPct, zoneOf } from '../domain/rest'

const mmss = (s: number) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`

export default function RestOverlay() {
  const { rest, stopRest, discardRest, minimizeRest, expandRest, adjustRest } = useUI()
  const { setField } = useStore()
  if (!rest.active && !rest.done) return null

  const b = { min: rest.min, ideal: rest.ideal, max: rest.max }
  const t = rest.done ? rest.done.elapsed : rest.elapsed
  const zone = rest.done ? rest.done.zone : zoneOf(t, b)
  const iPct = idealPct(b)
  const fillPct = frac(t, b) * 100

  const status = rest.done
    ? { under: 'registrado · corto', ideal: 'registrado · en rango ✓', over: 'registrado · de sobra' }[zone]
    : zone === 'under' ? `mínimo en ${mmss(b.min - t)}`
      : zone === 'ideal' ? (b.max - t <= 20 ? `máx en ${mmss(b.max - t)}` : 'en rango · dale cuando estés listo')
        : t >= 600 ? 'descanso largo' : 'de sobra · sin drama'

  const onListo = () => { const r = stopRest(); if (r?.setKey) setField(r.day, r.setKey, { rest: r.elapsed }) }

  // Minimized: a floating pill so the athlete can browse the app mid-rest. Timer keeps running.
  if (rest.active && rest.minimized && !rest.done) {
    return (
      <div className="restMini" data-zone={zone} role="group" aria-label="Descanso minimizado">
        <button className="restMiniMain" onClick={expandRest} aria-label="Ampliar descanso">
          <span className="restMiniDot" aria-hidden="true" />
          <span className="disp">{mmss(t)}</span>
          <span className="restMiniLbl">descanso</span>
        </button>
        <button className="restMiniGo" onClick={onListo}>Listo</button>
      </div>
    )
  }

  return (
    <div id="rest" role="group" aria-label="Temporizador de descanso">
      <div className="restRow">
        <div className="kick">descanso</div>
        {!rest.done && <button className="restMinBtn" onClick={minimizeRest} aria-label="Minimizar descanso">▾ minimizar</button>}
      </div>

      <div className="restBig" data-zone={zone} aria-hidden="true">{rest.done ? `${mmss(t)} ✓` : mmss(t)}</div>
      {!rest.done && (
        // Correct the counter when you started it late (or early): shifts the real elapsed time.
        <div className="row" style={{ justifyContent: 'center', gap: 10, marginTop: 4 }}>
          <button className="restMinBtn" onClick={() => adjustRest(-15)} aria-label="Quitar 15 segundos al descanso">-15 s</button>
          <button className="restMinBtn" onClick={() => adjustRest(15)} aria-label="Agregar 15 segundos al descanso">+15 s</button>
        </div>
      )}
      <div className="restStatus" data-zone={zone}>{status}</div>
      <span className="sr-only" aria-live="polite">{status}</span>

      <div className={`restMeter${rest.pulse ? ' pulse' : ''}`} role="progressbar"
        aria-valuemin={0} aria-valuemax={b.max} aria-valuenow={Math.min(t, b.max)}
        aria-valuetext={`${mmss(t)}, ${zone === 'under' ? 'aún corto' : zone === 'ideal' ? 'en tu rango ideal' : 'sobre el máximo'}`}>
        <div className="zones"><i className="z1" /><i className="z2" /><i className="z3" /></div>
        <div className="fill" data-zone={zone} style={{ width: `${fillPct}%` }} />
        <span className={`tick${t >= b.min ? ' lit' : ''}`} style={{ left: '38%' }} />
        <span className="tick ideal" style={{ left: `${iPct}%` }} />
        <span className="idealDot" style={{ left: `${iPct}%` }} />
        <span className="tick" style={{ left: '80%' }} />
      </div>
      <div className="restLabels" aria-hidden="true">
        <span className="rl" style={{ left: '38%' }}><em>mínimo</em><b>{mmss(b.min)}</b></span>
        <span className="rl ideal" style={{ left: `${iPct}%` }}><em>ideal</em><b>{mmss(b.ideal)}</b></span>
        <span className="rl" style={{ left: '80%' }}><em>máximo</em><b>{mmss(b.max)}</b></span>
      </div>

      {!rest.done && <>
        <button className="restGo" onClick={onListo}
          aria-label="Listo, registrar descanso e ir a la siguiente serie">Listo</button>
        <button className="restDiscard" onClick={discardRest}>descartar</button>
      </>}
    </div>
  )
}
